import { Component, OnInit, Input, AfterViewInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { AnalyticsService } from '../../../analytics/services/analytics.service';
import { ColorUtilityService } from '../../../analytics-core/services/color.utility.service';
import { AnalyticsDataModel } from '../../../analytics/models/analytics.datamodel';
import { EmitterService } from '../../../../core/utility/services/emitter.service';
import { EVENT_EMITTERS } from '../../../analytics/constants/analytics.constants';
import * as _ from 'underscore';
import * as Highcharts from 'highcharts';
import { retry } from 'rxjs/operators';

@Component({
	selector: 'app-social-demographics-v4',
	templateUrl: './social-demographics-v4.component.html',
	styleUrls: ['./social-demographics-v4.component.css']
})
export class SocialDemographicsV4Component implements OnInit, AfterViewInit, OnDestroy {
	@Input() colorsconfig: any;
	@Input() downloadconfig: any;
	@Input() key: string;
	@ViewChild('chartcontainer', { static: true }) chartContainer: ElementRef;

	public widgetDataSubscription;
	public responseData: any;
	public selectedLegend;
	public isClickRate: boolean;
	public tableLegend;
	public colorPalette;
	public tableData;
	public reloadSubscription;
	public hasData = true;
	public showLoad = true;
	protected navigateSlideSubscription;

	constructor(
		protected analyticsService: AnalyticsService,
		protected colorUtilityService: ColorUtilityService,
		protected analyticsDataModel: AnalyticsDataModel
	) {
		this.reloadSubscription = EmitterService.get(EVENT_EMITTERS.RELOAD).subscribe((response: any) => {
			this.requestData();
		});
	}

	ngOnInit() {
		this.colorsconfig = JSON.parse(decodeURI(this.colorsconfig));
		this.colorPalette = this.analyticsDataModel.partner.colorPalette;
		this.downloadconfig =
			typeof this.downloadconfig === 'string' ? JSON.parse(decodeURI(this.downloadconfig)) : this.downloadconfig;
	}

	ngAfterViewInit() {
		this.requestData();
	}

	ngOnDestroy() {
		if (this.reloadSubscription) {
			this.reloadSubscription.unsubscribe();
		}
		if (this.widgetDataSubscription) {
			this.widgetDataSubscription.unsubscribe();
		}
	}

	requestData() {
		this.hasData = true;
		this.showLoad = true;
		this.widgetDataSubscription = this.analyticsService.processCustomServiceRequest(this.key).subscribe((response: any) => {
			this.showLoad = false;
			if (response.success && response.data.length > 0) {
				this.hasData = true;
				this.processResponseData(response.data);
			} else {
				this.hasData = false;
			}
		});
	}

	processResponseData(responseData) {
		this.tableLegend = (<HTMLInputElement>document.getElementById('legendSelected')).value;
		this.selectedLegend = (<HTMLInputElement>document.getElementById('socialLegendSelected')).value;
		this.responseData = responseData;
		this.processGraphdata(this.selectedLegend);
		this.processTabledata(this.tableLegend);
	}

	processRequireddata() {
		var ageGroup = _.uniq(_.pluck(this.responseData, 'age_group'));
		const groupByGender = _.groupBy(this.responseData, 'gender');
		return { ageGroup: ageGroup, groupByGender: groupByGender };
	}

	processTabledata(tableLegend: string) {
		this.tableLegend = tableLegend;
		this.isClickRate = this.tableLegend == 'click_rate' ? true : false;
		const requiredData = this.processRequireddata();
		this.tableData = [];
		for (let i = 0; i < requiredData.ageGroup.length; i++) {
			let obj = {
				age_group: requiredData.ageGroup[i],
				male_data: _.where(this.responseData, { age_group: requiredData.ageGroup[i], gender: 'male' })[0][tableLegend],
				female_data: _.where(this.responseData, { age_group: requiredData.ageGroup[i], gender: 'female' })[0][
					tableLegend
				],
				unknown_data: _.where(this.responseData, { age_group: requiredData.ageGroup[i], gender: 'unknown' })[0][
					tableLegend
				]
			};
			this.tableData.push(obj);
		}
		let obj = {
			age_group: 'All',
			male_data: this.processLegenddata(requiredData.groupByGender['male'])[this.tableLegend],
			female_data: this.processLegenddata(requiredData.groupByGender['female'])[this.tableLegend],
			unknown_data: this.processLegenddata(requiredData.groupByGender['unknown'])[this.tableLegend]
		};
		this.tableData.push(obj);
	}
	processLegenddata(data) {
		let impressionsSum: number = 0;
		let clicksSum: number = 0;
		let totla_click_rate: any = 0;

		let total = _.reduce(data, (acc, val) => {
			switch (this.tableLegend) {
				case 'impressions':
					return { impressions: Number(acc[this.tableLegend]) + Number(val[this.tableLegend]) };
					break;
				case 'clicks':
					return { clicks: Number(acc[this.tableLegend]) + Number(val[this.tableLegend]) };
					break;
				case 'click_rate':
					impressionsSum += Number(val['impressions']);
					clicksSum += Number(val['clicks']);
					totla_click_rate = (clicksSum / impressionsSum) * 100;
					return { click_rate: totla_click_rate };
					break;
				case 'engagements':
					return { engagements: Number(acc[this.tableLegend]) + Number(val[this.tableLegend]) };
					break;
			}
		});
		return total;
	}
	processGraphdata(selectedLegend: string) {
		this.selectedLegend = selectedLegend;
		const requiredData = this.processRequireddata();
		const processedData = [];
		let index = 0;
		for (let gender in requiredData.groupByGender) {
			let obj = {
				name: this.titleCase(gender),
				data: _.pluck(requiredData.groupByGender[gender], selectedLegend).map(Number),
				color: this.colorPalette[this.colorsconfig[index].color + 'Color']
			};
			index++;
			processedData.push(obj);
		}
		this.drawGraph(processedData, requiredData.ageGroup);
	}

	titleCase(text) {
		return text.charAt(0).toUpperCase() + text.slice(1);
	}

	drawGraph(processedData, ageGroup) {
		Highcharts.chart(this.chartContainer.nativeElement, {
			chart: {
				type: 'bar'
			},
			title: {
				text: this.titleCase(this.selectedLegend) + ' by Demographic',
				align: 'left',
				x: 20,
				style: {
					fontSize: '12',
					fontWeight: '700'
				}
			},
			credits: {
				enabled: false
			},
			xAxis: [
				{
					categories: ageGroup,
					labels: {
						style: {
							fontSize: '12',
							fontWeight: '700'
						}
					},
					tickLength: 0
				}
			],
			tooltip: {
				enabled: true,
				useHTML: true,
				formatter: function () {
					return `Age ${this.key}<br/><div><span style="color: ${this.color}; font-size:20px">● </span><b>${this.series.name}: ${this.y}</b></div>`;
				}
			},
			yAxis: [
				{
					allowDecimals: false,
					min: 0,
					title: {
						text: ''
					}
				}
			],
			legend: {
				reversed: true
			},
			plotOptions: {
				series: {
					stacking: 'normal'
				}
			},
			series: processedData.reverse()
		});
	}
	downloadCSV(e) {
		e.preventDefault();
		this.analyticsService.downloadCSV(
			this.downloadconfig.csvQueryKey ? this.downloadconfig.csvQueryKey : this.key,
			this.downloadconfig.fileName
		);
	}
}
